/**
 * CustomerOtpGate — email verification screen for customer signups.
 *
 * Mirrors {@link StaffOtpGate} (countdown, persistent challenge, expired-
 * code recovery) but verifies a `signup`-type OTP that Supabase sends as
 * the verification token in the signup confirmation email.
 *
 * Lifecycle:
 *   1. The parent signup form calls `supabase.auth.signUp(...)` and then
 *      mounts this gate with the email it just registered. The signup email
 *      template includes the token, so the user can copy the code.
 *   2. We verify with `supabase.auth.verifyOtp({ type: "signup" })`, which
 *      both confirms the email AND establishes a session.
 *   3. On success we call `onVerified(session.user.id)` so the caller can
 *      route the customer into their account dashboard.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, Loader2, Mail, RefreshCw, X, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  OTP_RESEND_COOLDOWN_SEC,
  clearChallenge,
  formatMmSs,
  isExpiredOtpError,
  readChallenge,
  saveChallenge,
} from "@/lib/otpChallenge";

interface Props {
  email: string;
  /** Called on success with the new session's user id. */
  onVerified: (userId: string) => void;
  /** Called when the user gives up — caller usually returns to the form. */
  onCancel: () => void;
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

export default function CustomerOtpGate({ email, onVerified, onCancel }: Props) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorIsExpired, setErrorIsExpired] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [tick, setTick] = useState(Date.now());
  const [challengeExpiresAt, setChallengeExpiresAt] = useState<number | null>(null);
  const [challengeSentAt, setChallengeSentAt] = useState<number | null>(null);
  const restored = useRef(false);

  // Restore live challenge (or assume signup just sent one).
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const existing = readChallenge("customer-signup", email);
    if (existing) {
      setChallengeSentAt(existing.sentAt);
      setChallengeExpiresAt(existing.expiresAt);
    } else {
      // Signup email was just dispatched by the parent — record the window.
      const ch = saveChallenge("customer-signup", email);
      setChallengeSentAt(ch.sentAt);
      setChallengeExpiresAt(ch.expiresAt);
    }
  }, [email]);

  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-detect verification when the user taps the link in the email
  // (in the same browser). Supabase confirms the email + establishes a
  // session, which we poll for here.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        if (cancelled || !session?.user) return;
        const sessEmail = (session.user.email || "").toLowerCase();
        if (sessEmail === email.trim().toLowerCase() && session.user.email_confirmed_at) {
          clearChallenge("customer-signup", email);
          toast.success("Email verified — you're all set!");
          onVerified(session.user.id);
        }
      } catch { /* ignore */ }
    };
    check();
    const id = setInterval(check, 3000);
    const { data: sub } = supabase.auth.onAuthStateChange(() => { check(); });
    return () => { cancelled = true; clearInterval(id); sub.subscription.unsubscribe(); };
  }, [email, onVerified]);

  useEffect(() => {
    if (challengeExpiresAt && tick > challengeExpiresAt) {
      setChallengeExpiresAt(null);
      setChallengeSentAt(null);
      clearChallenge("customer-signup", email);
      if (!error) {
        setError("Your code has expired. Tap \"Resend code\" to get a new one.");
        setErrorIsExpired(true);
      }
    }
  }, [tick, challengeExpiresAt, email, error]);

  const validitySecondsLeft = useMemo(() => {
    if (!challengeExpiresAt) return 0;
    return Math.max(0, Math.floor((challengeExpiresAt - tick) / 1000));
  }, [challengeExpiresAt, tick]);

  const cooldownSecondsLeft = useMemo(() => {
    if (!challengeSentAt) return 0;
    return Math.max(0, Math.ceil((challengeSentAt + OTP_RESEND_COOLDOWN_SEC * 1000 - tick) / 1000));
  }, [challengeSentAt, tick]);

  async function resend() {
    setSending(true);
    setError(null);
    setErrorIsExpired(false);
    try {
      const { error: resendErr } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: (await import("@/lib/signupRedirect")).buildSignupRedirectUrl() },
      });
      if (resendErr) throw resendErr;
      const ch = saveChallenge("customer-signup", email);
      setChallengeSentAt(ch.sentAt);
      setChallengeExpiresAt(ch.expiresAt);
      setCode("");
      setAttemptsLeft(5);
      toast.success("New code sent — check your email.");
    } catch (e) {
      setError(getErrorMessage(e, "Could not resend the verification code."));
    } finally {
      setSending(false);
    }
  }

  async function handleVerify() {
    if (busy) return;
    const trimmed = code.trim();
    if (!/^\d{6,10}$/.test(trimmed)) {
      setError("Enter the verification code from your email.");
      setErrorIsExpired(false);
      return;
    }
    setBusy(true);
    setError(null);
    setErrorIsExpired(false);
    try {
      const { data, error: verifyErr } = await supabase.auth.verifyOtp({
        email,
        token: trimmed,
        type: "signup",
      });
      if (verifyErr || !data?.session) {
        const msg = getErrorMessage(verifyErr, "Invalid or expired code.");
        if (isExpiredOtpError(msg)) {
          clearChallenge("customer-signup", email);
          setChallengeExpiresAt(null);
          setChallengeSentAt(null);
          setCode("");
          setError("That code is no longer valid. Tap \"Resend code\" to get a new one.");
          setErrorIsExpired(true);
          return;
        }
        const remaining = Math.max(0, attemptsLeft - 1);
        setAttemptsLeft(remaining);
        if (remaining === 0) {
          toast.error("Too many incorrect codes. Please start over.");
          clearChallenge("customer-signup", email);
          onCancel();
          return;
        }
        throw verifyErr || new Error("Invalid code.");
      }
      toast.success("Email verified — you're all set!");
      clearChallenge("customer-signup", email);
      onVerified(data.session.user.id);
    } catch (e) {
      setError(getErrorMessage(e, "Verification failed."));
    } finally {
      setBusy(false);
    }
  }

  const canResend = !sending && cooldownSecondsLeft <= 0;
  const showFreshRequestCTA = errorIsExpired || (!challengeExpiresAt && !sending);

  return (
    <div className="bg-card rounded-2xl p-5 w-full space-y-4">
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
          <ShieldCheck className="w-6 h-6 text-primary" />
        </div>
        <h2 className="font-display text-base font-bold text-card-foreground">Verify your email</h2>
        <p className="text-xs text-muted-foreground mt-1">
          We sent a verification code to <span className="font-semibold text-foreground break-all">{email}</span>.
        </p>
      </div>

      {challengeExpiresAt && validitySecondsLeft > 0 && (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Code expires in</span>
          <span className="font-mono font-semibold text-foreground">{formatMmSs(validitySecondsLeft)}</span>
        </div>
      )}

      <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-foreground">
        <span className="font-semibold">Tip:</span> open the email and tap <span className="font-semibold">Verify Email</span> — we'll detect it automatically here. Or enter the code below.
      </div>

      <div className="space-y-3">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(null); setErrorIsExpired(false); }}
          onKeyDown={(e) => e.key === "Enter" && handleVerify()}
          placeholder="Enter code"
          maxLength={10}
          className="w-full bg-muted text-foreground rounded-xl px-4 py-3 text-center text-lg tracking-[0.5em] font-mono border border-border focus:ring-2 focus:ring-ring focus:outline-none"
        />
        {error && (
          <div className={`flex items-start gap-2 rounded-lg px-2.5 py-2 text-xs ${errorIsExpired ? "bg-destructive/10 text-destructive border border-destructive/30" : "text-destructive"}`}>
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        {attemptsLeft < 5 && !errorIsExpired && (
          <p className="text-[11px] text-muted-foreground">{attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} remaining.</p>
        )}

        <button
          onClick={handleVerify}
          disabled={busy || code.length < 6 || !challengeExpiresAt}
          className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {busy ? "Verifying..." : "Verify & continue"}
        </button>

        {showFreshRequestCTA && (
          <button
            onClick={resend}
            disabled={sending}
            className="w-full border border-primary/40 text-primary py-2.5 rounded-xl font-semibold text-xs hover:bg-primary/5 disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {sending ? "Sending..." : "Send a new code"}
          </button>
        )}

        <div className="flex items-center justify-between pt-1">
          <button
            onClick={resend}
            disabled={!canResend}
            className="text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline inline-flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" />
            {sending ? "Sending..." : cooldownSecondsLeft > 0 ? `Resend in ${cooldownSecondsLeft}s` : "Resend code"}
          </button>
          <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Use a different email
          </button>
        </div>

        <p className="text-[10px] text-muted-foreground text-center pt-2 inline-flex items-center gap-1 justify-center w-full">
          <Mail className="w-3 h-3" /> Codes expire in about 5 minutes.
        </p>
      </div>
    </div>
  );
}
